import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Loader2,
  FileSpreadsheet,
  Filter,
  X,
  RefreshCw,
  CreditCard,
  DollarSign
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function OnlinePaymentsReport() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [branches, setBranches] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [scanners, setScanners] = useState([]);

  // Filters
  const [filterBranch, setFilterBranch] = useState('');
  const [filterTheater, setFilterTheater] = useState('');
  const [filterScanner, setFilterScanner] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterPaymentMode, setFilterPaymentMode] = useState('online'); // online, both

  useEffect(() => {
    fetchBranches();
    fetchTheaters();
    fetchScanners();
    fetchBills();
  }, []);

  useEffect(() => {
    if (filterBranch || filterTheater || filterScanner || filterStartDate || filterEndDate || filterPaymentMode) {
      fetchBills();
    }
  }, [filterBranch, filterTheater, filterScanner, filterStartDate, filterEndDate, filterPaymentMode]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchTheaters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/theaters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTheaters(data);
      }
    } catch (error) {
      console.error('Error fetching theaters:', error);
    }
  };

  const fetchScanners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/scanners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScanners(data);
      }
    } catch (error) {
      console.error('Error fetching scanners:', error);
    }
  };

  const fetchBills = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filterBranch) params.append('branch_id', filterBranch);
      if (filterTheater) params.append('theater_id', filterTheater);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);

      const response = await fetch(`${BACKEND_URL}/api/bills?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        let data = await response.json();
        
        // Filter for online payments based on mode
        if (filterPaymentMode === 'online') {
          // Online only: Show bills with ONLY online payment (no cash)
          data = data.filter(bill => 
            bill.online_amount > 0 && (bill.cash_amount === 0 || !bill.cash_amount)
          );
        } else if (filterPaymentMode === 'both') {
          // Online + Mixed: Show bills with any online amount (pure online or mixed)
          data = data.filter(bill => bill.online_amount > 0);
        }
        
        // Filter by scanner if selected
        if (filterScanner) {
          data = data.filter(bill => bill.scanner_id === filterScanner);
        }

        // Sort by date (newest first)
        data.sort((a, b) => {
          const dateA = new Date(b.date);
          const dateB = new Date(a.date);
          return dateA - dateB;
        });
        
        setBills(data);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      setBills([]); // Set empty array on error
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch bills',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterBranch('');
    setFilterTheater('');
    setFilterScanner('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterPaymentMode('online');
  };

  const exportToExcel = () => {
    try {
      if (bills.length === 0) {
        toast({
          title: 'No Data',
          description: 'No bills available to export',
          variant: 'destructive'
        });
        return;
      }

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Online Payments Summary
      const summaryData = bills.map(bill => ({
        'Bill No': bill.bill_number,
        'Date': bill.date,
        'Branch': bill.branch_name,
        'Theater': bill.theater_name,
        'Time Slot': bill.time_slot,
        'Customer Name': bill.customer_name || 'N/A',
        'Customer Phone': bill.customer_phone || 'N/A',
        'Online Amount': bill.online_amount,
        'Cash Amount': bill.cash_amount || 0,
        'Total Bill Amount': bill.actual_amount,
        'Scanner': bill.scanner_name || 'N/A',
        'Scanner UPI ID': getScannerUPI(bill.scanner_id),
        'Payment Mode': bill.payment_mode,
        'Created By': bill.created_by_username
      }));

      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Online Payments');

      // Sheet 2: Scanner-wise Summary
      const scannerSummary = {};
      bills.forEach(bill => {
        const scannerKey = bill.scanner_name || 'Unknown Scanner';
        if (!scannerSummary[scannerKey]) {
          scannerSummary[scannerKey] = {
            'Scanner Name': scannerKey,
            'Scanner UPI ID': getScannerUPI(bill.scanner_id),
            'Number of Transactions': 0,
            'Total Online Amount': 0
          };
        }
        scannerSummary[scannerKey]['Number of Transactions']++;
        scannerSummary[scannerKey]['Total Online Amount'] += bill.online_amount;
      });

      const scannerSummaryData = Object.values(scannerSummary);
      const scannerWs = XLSX.utils.json_to_sheet(scannerSummaryData);
      XLSX.utils.book_append_sheet(wb, scannerWs, 'Scanner Summary');

      // Sheet 3: Daily Summary
      const dailySummary = {};
      bills.forEach(bill => {
        const dateKey = bill.date;
        if (!dailySummary[dateKey]) {
          dailySummary[dateKey] = {
            'Date': dateKey,
            'Number of Bills': 0,
            'Total Online Amount': 0,
            'Total Cash Amount': 0,
            'Total Amount': 0
          };
        }
        dailySummary[dateKey]['Number of Bills']++;
        dailySummary[dateKey]['Total Online Amount'] += bill.online_amount;
        dailySummary[dateKey]['Total Cash Amount'] += bill.cash_amount || 0;
        dailySummary[dateKey]['Total Amount'] += bill.actual_amount;
      });

      const dailySummaryData = Object.values(dailySummary).sort((a, b) => 
        new Date(b.Date) - new Date(a.Date)
      );
      const dailyWs = XLSX.utils.json_to_sheet(dailySummaryData);
      XLSX.utils.book_append_sheet(wb, dailyWs, 'Daily Summary');

      // Sheet 4: Branch-wise Summary
      const branchSummary = {};
      bills.forEach(bill => {
        const branchKey = bill.branch_name;
        if (!branchSummary[branchKey]) {
          branchSummary[branchKey] = {
            'Branch': branchKey,
            'Number of Transactions': 0,
            'Total Online Amount': 0
          };
        }
        branchSummary[branchKey]['Number of Transactions']++;
        branchSummary[branchKey]['Total Online Amount'] += bill.online_amount;
      });

      const branchSummaryData = Object.values(branchSummary);
      const branchWs = XLSX.utils.json_to_sheet(branchSummaryData);
      XLSX.utils.book_append_sheet(wb, branchWs, 'Branch Summary');

      // Generate filename
      let filename = 'Online_Payments_Report';
      if (filterStartDate && filterEndDate) {
        filename = `Online_Payments_${filterStartDate}_to_${filterEndDate}`;
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast({
        title: 'Success',
        description: `Exported ${bills.length} online payment records`
      });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({
        title: 'Error',
        description: 'Failed to export data',
        variant: 'destructive'
      });
    }
  };

  const getScannerUPI = (scannerId) => {
    const scanner = scanners.find(s => s.id === scannerId);
    return scanner?.upi_id || 'N/A';
  };

  const calculateTotals = () => {
    const totalOnline = bills.reduce((sum, bill) => sum + (bill.online_amount || 0), 0);
    const totalCash = bills.reduce((sum, bill) => sum + (bill.cash_amount || 0), 0);
    const totalAmount = bills.reduce((sum, bill) => sum + (bill.actual_amount || 0), 0);
    return { totalOnline, totalCash, totalAmount };
  };

  const totals = calculateTotals();

  // Group bills by scanner for easy viewing
  const billsByScanner = Array.isArray(bills) ? bills.reduce((acc, bill) => {
    const scannerName = bill.scanner_name || 'Unknown Scanner';
    if (!acc[scannerName]) {
      acc[scannerName] = [];
    }
    acc[scannerName].push(bill);
    return acc;
  }, {}) : {};

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-8 w-8 text-blue-600" />
          Online Payments Report
        </h1>
        <p className="text-gray-500 mt-2">Track and reconcile online payments with bank statements</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {(user?.role === 'admin' || user?.role === 'auditor') && (
              <div>
                <Label htmlFor="filter-branch">Branch</Label>
                <Select value={filterBranch || "all"} onValueChange={(value) => setFilterBranch(value === "all" ? "" : value)}>
                  <SelectTrigger id="filter-branch">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="filter-theater">Theater</Label>
              <Select value={filterTheater || "all"} onValueChange={(value) => setFilterTheater(value === "all" ? "" : value)}>
                <SelectTrigger id="filter-theater">
                  <SelectValue placeholder="All Theaters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Theaters</SelectItem>
                  {theaters.map(theater => (
                    <SelectItem key={theater.id} value={theater.id}>{theater.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter-scanner">Payment Scanner</Label>
              <Select value={filterScanner || "all"} onValueChange={(value) => setFilterScanner(value === "all" ? "" : value)}>
                <SelectTrigger id="filter-scanner">
                  <SelectValue placeholder="All Scanners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scanners</SelectItem>
                  {scanners.map(scanner => (
                    <SelectItem key={scanner.id} value={scanner.id}>
                      {scanner.name} ({scanner.upi_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter-payment-mode">Payment Type</Label>
              <Select value={filterPaymentMode} onValueChange={setFilterPaymentMode}>
                <SelectTrigger id="filter-payment-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online Only</SelectItem>
                  <SelectItem value="both">Online + Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter-start-date">Start Date</Label>
              <Input
                id="filter-start-date"
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="filter-end-date">End Date</Label>
              <Input
                id="filter-end-date"
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={fetchBills} size="sm" variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={clearFilters} size="sm" variant="outline">
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
            <Button 
              onClick={exportToExcel} 
              size="sm" 
              className="ml-auto"
              disabled={bills.length === 0}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export to Excel ({bills.length} records)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {user?.role === 'auditor' ? (
        // Auditor: Show only transaction count
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-6 max-w-md">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Transactions</p>
                  <p className="text-2xl font-bold">{bills.length}</p>
                </div>
                <CreditCard className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        // Admin: Show all 4 cards
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Transactions</p>
                  <p className="text-2xl font-bold">{bills.length}</p>
                </div>
                <CreditCard className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Online Amount</p>
                  <p className="text-2xl font-bold text-green-600">₹{totals.totalOnline.toFixed(2)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Cash Amount</p>
                  <p className="text-2xl font-bold text-orange-600">₹{totals.totalCash.toFixed(2)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Grand Total</p>
                  <p className="text-2xl font-bold text-blue-600">₹{totals.totalAmount.toFixed(2)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bills Table */}
      <Card>
        <CardHeader>
          <CardTitle>Online Payment Transactions ({bills.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : bills.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No online payment transactions found. Adjust filters to see data.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Theater</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Slot</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Online Amt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cash Amt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scanner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UPI ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{bill.date}</td>
                      <td className="px-4 py-3 text-blue-600 font-medium">{bill.bill_number}</td>
                      <td className="px-4 py-3 text-gray-900">{bill.branch_name}</td>
                      <td className="px-4 py-3 text-gray-900">{bill.theater_name}</td>
                      <td className="px-4 py-3 text-gray-900">{bill.time_slot}</td>
                      <td className="px-4 py-3 text-gray-900">
                        <div>{bill.customer_name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{bill.customer_phone || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        ₹{bill.online_amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        ₹{(bill.cash_amount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{bill.scanner_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {getScannerUPI(bill.scanner_id)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2">
                  <tr>
                    <td colSpan="6" className="px-4 py-3 text-right font-semibold text-gray-900">
                      Totals:
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">
                      ₹{totals.totalOnline.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">
                      ₹{totals.totalCash.toFixed(2)}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scanner-wise Breakdown */}
      {!loading && bills.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Scanner-wise Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(billsByScanner).map(([scannerName, scannerBills]) => {
                const scannerTotal = scannerBills.reduce((sum, bill) => sum + bill.online_amount, 0);
                const scannerId = scannerBills[0]?.scanner_id;
                const scannerUPI = getScannerUPI(scannerId);

                return (
                  <div key={scannerName} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">{scannerName}</h3>
                        <p className="text-sm text-gray-500">UPI: {scannerUPI}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">{scannerBills.length} transactions</p>
                        <p className="text-lg font-bold text-green-600">₹{scannerTotal.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default OnlinePaymentsReport;
